import React, { useState, useEffect } from 'react';
import { doc, updateDoc, collection, query, where, getDocs, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { User, Mail, Shield, Settings, Save, X, Crown } from 'lucide-react';
import './AdminProfileManagement.css';

function AdminProfileManagement({ user }) {
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    role: 'admin',
    permissions: {
      canViewOrders: true,
      canUpdateOrderStatus: true,
      canManageProducts: true,
      canViewDashboard: true,
      canManageUsers: false
    }
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [adminExists, setAdminExists] = useState(false);

  useEffect(() => {
    loadAdminProfile();
    checkIfAdminExists();
  }, [user]);

  const checkIfAdminExists = async () => {
    if (!user) return;

    try {
      const adminQuery = query(
        collection(db, 'adminProfiles'),
        where('userId', '==', user.uid)
      );
      
      const adminSnapshot = await getDocs(adminQuery);
      setAdminExists(!adminSnapshot.empty);
    } catch (error) {
      console.error('Error checking admin profile:', error);
    }
  };

  const loadAdminProfile = async () => {
    if (!user) return;

    try {
      setProfileData(prev => ({
        ...prev,
        name: user.displayName || '',
        email: user.email || ''
      }));

      // Load admin profile from Firestore
      const adminQuery = query(
        collection(db, 'adminProfiles'),
        where('userId', '==', user.uid)
      );
      
      const adminSnapshot = await getDocs(adminQuery);
      if (!adminSnapshot.empty) {
        const adminDoc = adminSnapshot.docs[0];
        const adminInfo = adminDoc.data();
        setProfileData(prev => ({
          ...prev,
          ...adminInfo
        }));
      }
    } catch (error) {
      console.error('Error loading admin profile:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      if (name.startsWith('permission_')) {
        const permissionKey = name.replace('permission_', '');
        setProfileData(prev => ({
          ...prev,
          permissions: {
            ...prev.permissions,
            [permissionKey]: checked
          }
        }));
      }
    } else {
      setProfileData({
        ...profileData,
        [name]: value
      });
    }
  };

  const showMessage = (text, type = 'success') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => setMessage(''), 5000);
  };

  const saveAdminProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const adminProfile = {
        ...profileData,
        userId: user.uid,
        updatedAt: new Date(),
        lastLogin: new Date(),
        isActive: true
      };

      if (adminExists) {
        // Update existing admin profile
        const adminQuery = query(
          collection(db, 'adminProfiles'),
          where('userId', '==', user.uid)
        );
        const adminSnapshot = await getDocs(adminQuery);
        if (!adminSnapshot.empty) {
          const adminDoc = adminSnapshot.docs[0];
          await updateDoc(doc(db, 'adminProfiles', adminDoc.id), adminProfile);
        }
      } else {
        // Create new admin profile
        await setDoc(doc(db, 'adminProfiles', user.uid), adminProfile);
        setAdminExists(true);
      }

      showMessage('Admin profile saved successfully!', 'success');
    } catch (error) {
      console.error('Error saving admin profile:', error);
      showMessage('Error saving admin profile', 'error');
    } finally {
      setLoading(false);
    }
  };

  const authorizeAdmin = async () => {
    setLoading(true);
    try {
      await setDoc(doc(db, 'adminProfiles', user.uid), {
        userId: user.uid,
        email: user.email,
        name: profileData.name || user.displayName || 'Admin',
        role: 'admin',
        permissions: profileData.permissions,
        isActive: true,
        isAuthorized: true,
        authorizedAt: new Date(),
        updatedAt: new Date(),
        lastLogin: new Date()
      });

      setAdminExists(true);
      showMessage('Admin authorized successfully! You can now receive orders and update statuses.', 'success');
    } catch (error) {
      console.error('Error authorizing admin:', error);
      showMessage('Error authorizing admin', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-profile-management">
      <div className="admin-profile-header">
        <Crown size={32} className="admin-icon" />
        <div>
          <h2>Admin Profile Management</h2>
          <p>Manage your admin account and permissions</p>
        </div>
      </div>

      {message && (
        <div className={`message ${messageType}`}>
          {message}
        </div>
      )}

      {!adminExists ? (
        <div className="authorization-card">
          <div className="authorization-content">
            <Shield size={48} className="shield-icon" />
            <h3>Complete Admin Authorization</h3>
            <p>
              You need to create and authorize your admin profile to receive orders 
              and update order statuses. This will give you full access to the admin dashboard.
            </p>
            
            <div className="permission-summary">
              <h4>You'll have access to:</h4>
              <ul>
                <li>📊 View dashboard and analytics</li>
                <li>📦 View and manage customer orders</li>
                <li>🔄 Update order status (pending → processing → shipped → delivered)</li>
                <li>👕 Manage products and inventory</li>
                <li>📈 View sales charts and reports</li>
              </ul>
            </div>

            <button
              onClick={authorizeAdmin}
              className="authorize-button"
              disabled={loading}
            >
              {loading ? 'Authorizing...' : 'Authorize Admin Account'}
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={saveAdminProfile} className="admin-profile-form">
          <div className="form-section">
            <h3>Basic Information</h3>
            <div className="form-group">
              <label htmlFor="name">
                <User size={16} />
                Full Name
              </label>
              <input
                id="name"
                name="name"
                type="text"
                value={profileData.name}
                onChange={handleChange}
                placeholder="Admin Name"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">
                <Mail size={16} />
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={profileData.email}
                onChange={handleChange}
                placeholder="admin@trinitees.com"
                required
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Permissions</h3>
            <div className="permissions-grid">
              <label className="permission-item">
                <input
                  type="checkbox"
                  name="permission_canViewOrders"
                  checked={profileData.permissions.canViewOrders}
                  onChange={handleChange}
                />
                <span>View Orders</span>
              </label>

              <label className="permission-item">
                <input
                  type="checkbox"
                  name="permission_canUpdateOrderStatus"
                  checked={profileData.permissions.canUpdateOrderStatus}
                  onChange={handleChange}
                />
                <span>Update Order Status</span>
              </label>

              <label className="permission-item">
                <input
                  type="checkbox"
                  name="permission_canManageProducts"
                  checked={profileData.permissions.canManageProducts}
                  onChange={handleChange}
                />
                <span>Manage Products</span>
              </label>

              <label className="permission-item">
                <input
                  type="checkbox"
                  name="permission_canViewDashboard"
                  checked={profileData.permissions.canViewDashboard}
                  onChange={handleChange}
                />
                <span>View Dashboard</span>
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="save-button" disabled={loading}>
              <Save size={16} />
              {loading ? 'Saving...' : 'Save Profile'}
            </button>
          </div>
        </form>
      )}

      {adminExists && (
        <div className="status-card">
          <div className="status-indicator">
            <Shield size={24} className="verified-icon" />
            <div>
              <h4>Admin Account Authorized</h4>
              <p>You have full access to receive orders and update order statuses</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminProfileManagement;
